import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth, clearGenieConversationSessionStorage, markWelcomeDialogPending } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import ErrorDialog from '../components/ui/ErrorDialog';
import { Mail } from 'lucide-react';
import toast from 'react-hot-toast';

function Verify2FA() {
  const [loading, setLoading] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const { setUser, setIsAuthenticated, setPasswordResetRequired } = useAuth();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const codeValue = watch('code', '');

  const handleCodeChange = (e) => {
    const upperValue = e.target.value.toUpperCase();
    setValue('code', upperValue, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    if (!email) {
      setErrorMessage('Email is required. Please return to the login page.');
      setShowErrorDialog(true);
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    setLoading(true);
    try {
      // Ensure code is uppercase before submitting
      const code = (data.code || '').toUpperCase();
      await authService.verify2FALogin(email, code);
      
      // Session cookie is set automatically by the browser
      // Get user data and update context
      const userData = await authService.getCurrentUser();
      if (setUser) setUser(userData);
      if (setIsAuthenticated) setIsAuthenticated(true);
      if (setPasswordResetRequired) setPasswordResetRequired(userData.password_reset || false);

      clearGenieConversationSessionStorage();
      if (userData.show_welcome !== false) {
        markWelcomeDialogPending();
      }
      await organizationsService.refreshOrganizationsAfterLogin();

      toast.success('Login successful!');
      
      // Redirect based on password reset requirement
      if (userData.password_reset) {
        navigate('/profile');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Invalid verification code. Please check your email and try again.';
      setErrorMessage(errorMessage);
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">The Cube</h1>
          <p className="mt-6 text-sm text-gray-600">
            A verification code has been emailed to you at {email}
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                Verification code
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('code', {
                    required: 'Verification code is required',
                    pattern: {
                      value: /^[A-Z0-9]{6}$/,
                      message: 'Code must be 6 characters (A-Z, 0-9)',
                    },
                  })}
                  id="code"
                  type="text"
                  autoComplete="off"
                  maxLength={6}
                  value={codeValue}
                  onChange={handleCodeChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-hairline rounded-lg placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 sm:text-sm uppercase"
                  placeholder="Enter 6-digit code"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              {errors.code && (
                <p className="mt-2 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Verification Error"
        message={errorMessage}
      />
    </div>
  );
}

export default Verify2FA;

